import type { CarouselButtonConfig, CarouselItem } from '../../types';
import styles from './DisplayCard.module.css';

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  );
}

export interface DisplayCardProps {
  item: CarouselItem;
  onClick?: (item: CarouselItem) => void;
  variant?: 'horizontal' | 'vertical';
  button?: CarouselButtonConfig;
  onButtonClick?: (item: CarouselItem, formId: string) => void;
}

export function DisplayCard({ item, onClick, variant = 'horizontal', button, onButtonClick }: DisplayCardProps) {
  const handleClick = () => {
    onClick?.(item);
  };

  if (variant === 'vertical') {
    return (
      <div
        className={styles.verticalCard}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      >
        <div className={styles.verticalInfo}>
          <p className={styles.verticalTitle}>{item.title}</p>
          {item.description && (
            <p className={styles.verticalDescription}>{item.description}</p>
          )}
          {(item.primaryValue || item.secondaryValue) && (
            <div className={styles.verticalValueRow}>
              {item.primaryValue && (
                <p className={styles.verticalPrimaryValue}>{item.primaryValue}</p>
              )}
              {item.secondaryValue && (
                <p className={styles.verticalSecondaryValue}>{item.secondaryValue}</p>
              )}
            </div>
          )}
        </div>
        {item.badge ? (
          <span className={styles.badge}>{item.badge}</span>
        ) : (
          <ChevronRightIcon className={styles.chevronIcon} />
        )}
      </div>
    );
  }

  return (
    <div
      className={styles.card}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      {item.imageUrl && (
        <div className={styles.imageContainer}>
          <img
            src={item.imageUrl}
            alt={item.title}
            className={styles.image}
          />
          {item.badge && <span className={styles.imageBadge}>{item.badge}</span>}
        </div>
      )}
      <div className={styles.info}>
        <p className={styles.title}>{item.title}</p>
        {item.description && (
          <p className={styles.description}>{item.description}</p>
        )}
        {(item.primaryValue || item.secondaryValue) && (
          <div className={styles.valueRow}>
            {item.primaryValue && (
              <p className={styles.primaryValue}>{item.primaryValue}</p>
            )}
            {item.secondaryValue && (
              <p className={styles.secondaryValue}>{item.secondaryValue}</p>
            )}
          </div>
        )}
      </div>
      {button && (
        <button
          className={styles.cardButton}
          onClick={(e) => {
            e.stopPropagation();
            onButtonClick?.(item, button.formId);
          }}
          type="button"
        >
          {button.title}
        </button>
      )}
    </div>
  );
}
